import axios from "axios";
import { Toast } from "vant";
import store from "@/store";
import { buildAuthToken, isEncryptableBody, serializeBody } from "./authCrypto";
import { showTimeErrorDialog } from "@/components/TimeErrorDialog.vue";
import { syncClockFromResponse } from "./clockSync";
const service = axios.create({
  // 请求根地址
  baseURL: '/',
  timeout: "50000",
});

// 业务错误提示抑制截止时间：避免业务页面 catch 中的 Toast 覆盖"时间不准"弹窗
let suppressToastUntil = 0;

const rawToastFail = Toast.fail.bind(Toast);
Toast.fail = function (...args) {
  if (Date.now() < suppressToastUntil) {
    return;
  }
  return rawToastFail(...args);
};

// 提取请求方法名
export function extractMethodName(postData) {
  let methodName = '';
  try {
    if (!postData) {
      return '';
    }
    let jsonData = {};
    if (typeof postData === 'string') {
      jsonData = JSON.parse(postData);
    } else {
      jsonData = postData;
    }
    if (!jsonData.Action || !jsonData.Method) {
      return '';
    }
    const action = jsonData.Action;
    const method = jsonData.Method;
    if ((action === 'QueryData' || action === 'OperateData') && jsonData.SchemaId) {
      methodName = `${action}-${method}[SchemaId=${jsonData.SchemaId}]`;
    } else {
      methodName = `${action}-${method}`;
    }
  } catch (e) {
    return '';
  }
  return methodName;
}

// 请求拦截
service.interceptors.request.use(
  // 在发送请求之前做些什么
  async (config) => {
    // 设置请求头
      const token = sessionStorage.getItem("AuthToken");
      if (token) {
        config.headers['authToken'] = sessionStorage.getItem("AuthToken"); // 设置请求头
      } else {
        config.headers['authToken'] = '///';
      }
      config.headers['userid'] = sessionStorage.getItem('userid') || '',

      config.headers['Content-Type'] = "application/json;charset=utf-8";

      // 新增加密 token（{p,k,iv,v}），所有请求统一携带
      // 总开关：仅七彩石配置 isDcopsToken=1 时才添加，否则不携带
      // 若调用方已显式设置 headers.token 则跳过，便于按接口控制
      const isDcopsTokenOn = String(store.getters.getConfigItem('isDcopsToken')) === '1';
      if (isDcopsTokenOn && !config.headers['token']) {
        try {
          if (isEncryptableBody(config.data)) {
            // 明文 = 序列化后的 body（与加密进 p 的明文完全一致，保证服务端 sha256 校验通过）
            const plaintext = serializeBody(config.data);
            // 普通对象 body 替换为序列化字符串，保证实际发送的 body 字节与明文一致
            if (config.data != null && typeof config.data === 'object') {
              config.data = plaintext;
            }
            config.headers['token'] = await buildAuthToken(plaintext);
          } else {
            console.warn('[authCrypto] 请求体为 FormData/Blob 等类型，无法加密对齐，跳过 token：', config.url);
          }
        } catch (e) {
          // 序列化（循环引用/BigInt）或公钥缺失/加载失败时，不阻塞业务请求
          console.error(`[authCrypto] ${config.url} 生成加密 token 失败（${e.message}），本次请求不携带 token：`, e);
        }
      }
    // 记录本机发出请求的时间，响应到达后用它配合 Date 头估算服务器时钟偏移
    config.__clientSendTime = Date.now();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截
service.interceptors.response.use(
  (response) => {
    // console.log(response,"response请求");

    // authToken会过期 1小时，判断当前请求的status为401就需要重新获取token
    const status = response?.data?.status;
    const msg = response?.data?.msg;
    
    if (status == 401) {
      Toast.setDefaultOptions({ duration: 1500 });
      Toast.fail(response.data.msg + ', 正在重新登录');
      const AGENT_ID = process.env.NODE_ENV === 'development' ? '1000036' : '1000035';
      const HOST = process.env.NODE_ENV === 'development' ? 'https://test.otob.dcops.qq.com' : 'https://otob.dcops.qq.com';

      location.href = `${HOST}/test-page`
    }
    // 手机系统时间不准确：服务端校验时间戳过期（timestamp expired）
    // 对齐 AWS SDK 的 correctClockSkew：先用响应 Date 头算出时间差、纠正本地偏移，
    // 再自动重试一次；正常情况下用户完全无感，不再需要手动改手机时间
    if (status == 304 && msg === 'timestamp expired') {
      const requestConfig = response.config || {};
      const synced = syncClockFromResponse(
        response.headers && (response.headers.date || response.headers.Date),
        requestConfig.__clientSendTime,
        Date.now(),
        // 强制校准：原有基准可能已被用户中途改系统时间打乱
        true
      );

      if (synced && !requestConfig.__clockSkewRetried) {
        requestConfig.__clockSkewRetried = true;
        // 清掉旧 token，让请求拦截器用校正后的时间戳重新生成
        if (requestConfig.headers) {
          delete requestConfig.headers['token'];
        }
        requestConfig.__clientSendTime = undefined;
        // 静默重试：请求体会沿用首次已序列化的明文，sha256 校验保持一致
        return service.request(requestConfig);
      }

      // 兜底：拿不到 Date 头，或按服务器时间重试后仍然过期（说明不是本机时钟问题）
      // 3 秒内抑制业务层 catch 中的错误 Toast，避免 Toast 覆盖弹窗
      suppressToastUntil = Date.now() + 3000;
      Toast.clear();
      // 弹窗内部为单例，并发请求重复触发也只会显示一个
      showTimeErrorDialog();
      // 中断业务链：避免业务代码继续处理空 data（如 undefined.map）抛错，
      // 其 catch 中的提示会被上面的抑制窗口拦截
      return Promise.reject(new Error('timestamp expired'));
    }

    if (msg && status !== 200) {
      response.data.msg = msg + '\n请联系开发人员处理';
    } else if (response.data && status !== 200) {
      response.data = {
        ...(response.data || {}),
        msg: '请联系开发人员处理。'
      }
    } else {
      Toast.fail('\n请稍等60秒后再试。如果错误重复出现，请联系philyuliu(刘宇)处理。');
    }
    // return response.data.Data;  
    return response;
  },
  (error) => {
    // if (error.code == 'ECONNABORTED') {
      console.log(error)
      Toast.setDefaultOptions({ duration: 2000 });
      Toast.fail(error.message + '\n请稍等60秒后再试。如果错误重复出现，请联系philyuliu(刘宇)处理。');
      error.message = error.message + '\n请稍等60秒后再试。如果错误重复出现，请联系philyuliu(刘宇)处理。';
    // }
    return Promise.reject(error);
  }
);

export default service;

// import axios from "axios";

// const ins = axios.create({
//   baseURL: "/api",
//   timeout: 6000,
// });

// // 请求拦截
// ins.interceptors.request.use(
//   (config) => {
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// // 响应拦截
// ins.interceptors.response.use(
//   (response) => {
//     return response;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// const request = async (config) => {

//   const { url = "", data = {}, method = "get" } = config;

//   const requestIns = () => {
//     switch (method.toLocaleUpperCase()) {
//       case "GET":
//         return ins.get(url, { params: data });
//       case "POST":
//         return ins.post(url, data);
//       case "PUT":
//         return ins.put(url, data);
//       case "DELETE":
//         return ins.delete(url, { data });
//       default:
//         return ins.get(url, { params: data });
//     }
//   };

//   try {
//     const { data } = await requestIns();
//     if (data.code == !200) {
//       throw Error(data.message);
//     }
    
//     return data || [];
    
//   } catch (error) {
//     // const errorMsg =
//     //   error?.message?.replace(/(.+)with status code [0-9]+$/, "$1") || "";
//       Toast.fail(error)
//       // Notify({ type: 'warning', message: errorMsg });
//     // return {
//     //     error,
//     //     requestError: true,
//     // };
//     return false;
//   }
// };

// export default request;
